import React from "react";
import { DocumentColor, MailColor, VideoColor } from "@fluentui/react-icons";
import { tokens } from "@fluentui/react-components";
import { AttachmentVisual } from "./AttachmentListComponentTypes";

const ICON_SIZE = 20;

/**
 * Module-level counter for generating unique SVG gradient IDs.
 * Needed because @fluentui/react-icons Color icons hardcode gradient IDs, causing
 * SVG gradient ID collisions when multiple instances of the same icon are rendered
 * simultaneously — all but the last instance lose their gradient fill and appear
 * invisible. Using per-instance IDs (via useRef + counter) isolates each SVG's
 * gradient definitions from all others on the page.
 */
let _folderIconIdCounter = 0;

/**
 * Pixel-perfect replica of the Fluent UI DocumentFolderColor (20px) icon with
 * per-instance unique gradient IDs to prevent SVG gradient ID collision.
 */
const DocumentFolderColorIcon = ({ fontSize = ICON_SIZE, className }: { fontSize?: number; className?: string }) => {
    const idRef = React.useRef<string | null>(null);
    if (idRef.current === null) {
        idRef.current = `_dfci_${++_folderIconIdCounter}`;
    }
    const uid = idRef.current;
    return (
        <svg
            width={fontSize}
            height={fontSize}
            fill="none"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
            aria-hidden="true"
            role="presentation"
        >
            <path d="M6 6c0-1.1.9-2 2-2h6a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V6Z" fill={`url(#${uid}a)`} />
            <path d="M6 6c0-1.1.9-2 2-2h6a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V6Z" fill={`url(#${uid}b)`} />
            <path d="M4 4c0-1.1.9-2 2-2h6a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4Z" fill={`url(#${uid}c)`} />
            <path
                d="M5 7a2 2 0 0 0-2 2v6.5A2.5 2.5 0 0 0 5.5 18h9a2.5 2.5 0 0 0 2.5-2.5V14a2 2 0 0 0-2-2h-1.88a1 1 0 0 1-.7-.3L8.28 7.6A2 2 0 0 0 6.88 7H5Z"
                fill={`url(#${uid}d)`}
            />
            <defs>
                <linearGradient id={`${uid}a`} x1="17.5" y1="19.5" x2="19.69" y2="5.88" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#BB45EA" />
                    <stop offset="1" stopColor="#9C6CFE" />
                </linearGradient>
                <linearGradient id={`${uid}b`} x1="16" y1="8.5" x2="13.5" y2="8.5" gradientUnits="userSpaceOnUse">
                    <stop offset=".34" stopColor="#5750E2" stopOpacity="0" />
                    <stop offset="1" stopColor="#5750E2" />
                </linearGradient>
                <linearGradient id={`${uid}d`} x1="6" y1="7" x2="6" y2="22" gradientUnits="userSpaceOnUse">
                    <stop offset=".24" stopColor="#FFD638" />
                    <stop offset=".64" stopColor="#FAB500" />
                    <stop offset=".99" stopColor="#CA6407" />
                </linearGradient>
                <radialGradient
                    id={`${uid}c`}
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="rotate(-60.52 15.5 1) scale(13.2098 18.9134)"
                >
                    <stop offset=".23" stopColor="#2764E7" />
                    <stop offset=".68" stopColor="#5CD1FF" />
                    <stop offset="1" stopColor="#6CE0FF" />
                </radialGradient>
            </defs>
        </svg>
    );
};

/**
 * Module-level counter shared by all gradient icons produced by createGradientIcon.
 * Each mounted instance gets a unique numeric suffix so gradient IDs never collide
 * when multiple files of the same type appear in the attachment list simultaneously.
 */
let _gradientIconIdCounter = 0;

/** Custom Color-style icon for PDF files, using DocumentPdfFilled paths with a red-to-orange gradient. */
const DocumentPdfColorIcon = ({ fontSize = ICON_SIZE, className }: { fontSize?: number; className?: string }) => {
    const idRef = React.useRef<string | null>(null);
    if (idRef.current === null) {
        idRef.current = `_pdfi_${++_gradientIconIdCounter}`;
    }
    const uid = idRef.current;
    return (
        <svg
            width={fontSize}
            height={fontSize}
            fill="none"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
            aria-hidden="true"
            role="presentation"
        >
            <defs>
                <linearGradient id={uid} x1="10" y1="2" x2="10" y2="18" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#FF6C6C" />
                    <stop offset="1" stopColor="#FE8548" />
                </linearGradient>
            </defs>
            {/* Document body + page fold */}
            <path
                d="M10 6.5c0 .83.67 1.5 1.5 1.5H16v8.5c0 .83-.67 1.5-1.5 1.5h-1.59c.06-.16.09-.32.09-.5v-1h.5a1.5 1.5 0 0 0 1.02-2.6A1.5 1.5 0 0 0 14 11h-2.5a1.5 1.5 0 0 0-1.37.89A2.99 2.99 0 0 0 8 11H6.5a1.5 1.5 0 0 0-1.32.8A2.75 2.75 0 0 0 4 11.1V3.5C4 2.67 4.67 2 5.5 2H10v4.5Zm5.75.5H11.5a.5.5 0 0 1-.5-.5V2.25L15.75 7Z"
                fill={`url(#${uid})`}
            />
            {/* "PDF" text — adapts to light/dark mode */}
            <path
                d="M3 12a2 2 0 1 1 0 4H2v1.5a.5.5 0 0 1-1 0v-5c0-.28.22-.5.5-.5H3Zm-1 3h1a1 1 0 1 0 0-2H2v2Zm6-3a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2H6.5a.5.5 0 0 1-.5-.5v-5c0-.28.22-.5.5-.5H8Zm-1 5h1a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1H7v4Zm7.1-4.99a.5.5 0 0 1 0 .98L14 13h-2v1.5h1.5a.5.5 0 0 1 0 1H12v2a.5.5 0 0 1-1 0v-5c0-.28.22-.5.5-.5H14l.1.01Z"
                fill={tokens.colorNeutralForeground1}
            />
        </svg>
    );
};

/**
 * Factory for creating gradient SVG icon components from Fluent icon path data.
 * Each mounted instance generates a unique gradient ID (via lazy useRef init) so
 * multiple files of the same type in the attachment list never collide.
 * The displayName is set from gradientIdBase so React DevTools shows a meaningful
 * component name (e.g. "ic_sound_color") instead of the generic "GradientIcon".
 */
function createGradientIcon(
    gradientIdBase: string,
    pathData: string,
    topColor: string,
    bottomColor: string
): React.FC<{ fontSize?: number; className?: string }> {
    const GradientIcon = ({ fontSize = ICON_SIZE, className }: { fontSize?: number; className?: string }) => {
        const idRef = React.useRef<string | null>(null);
        if (idRef.current === null) {
            idRef.current = `_gi_${++_gradientIconIdCounter}`;
        }
        const uid = idRef.current;
        return (
            <svg
                width={fontSize}
                height={fontSize}
                fill="none"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
                className={className}
                aria-hidden="true"
                role="presentation"
            >
                <defs>
                    <linearGradient id={uid} x1="10" y1="2" x2="10" y2="18" gradientUnits="userSpaceOnUse">
                        <stop stopColor={topColor} />
                        <stop offset="1" stopColor={bottomColor} />
                    </linearGradient>
                </defs>
                <path d={pathData} fill={`url(#${uid})`} />
            </svg>
        );
    };
    GradientIcon.displayName = gradientIdBase;
    return GradientIcon;
}

// --- Gradient icon definitions (one-liner per icon) ---

const SoundWaveCircleColorIcon = createGradientIcon(
    "ic_sound_color",
    "M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0ZM6 8.5a.5.5 0 0 0-.5.5v2a.5.5 0 0 0 1 0V9a.5.5 0 0 0-.5-.5Zm5.5 0a.5.5 0 0 0-1 0v3a.5.5 0 0 0 1 0v-3ZM8.5 7a.5.5 0 0 0-.5.5v5a.5.5 0 0 0 1 0v-5a.5.5 0 0 0-.5-.5Zm5.5.5a.5.5 0 0 0-1 0v5a.5.5 0 0 0 1 0v-5Z",
    "#6CE0FF",
    "#4894FE"
);

const CodeBlockColorIcon = createGradientIcon(
    "ic_code_block_color",
    "M6 3a3 3 0 0 0-3 3v8a3 3 0 0 0 3 3h8a3 3 0 0 0 3-3V6a3 3 0 0 0-3-3H6Zm2.85 4.85L6.71 10l2.14 2.15a.5.5 0 0 1-.7.7l-2.5-2.5a.5.5 0 0 1 0-.7l2.5-2.5a.5.5 0 1 1 .7.7Zm3-.7 2.5 2.5c.2.2.2.5 0 .7l-2.5 2.5a.5.5 0 0 1-.7-.7L13.29 10l-2.14-2.15a.5.5 0 0 1 .7-.7Z",
    "#FFA96C",
    "#FED148"
);

const CubeColorIcon = createGradientIcon(
    "ic_cube_color",
    "M8.7 2.48a3.5 3.5 0 0 1 2.6 0l5.76 2.3c.57.23.94.78.94 1.4v7.64a1.5 1.5 0 0 1-.94 1.4l-5.76 2.3a3.5 3.5 0 0 1-2.6 0l-5.76-2.3a1.5 1.5 0 0 1-.94-1.4V6.18c0-.62.37-1.17.94-1.4l5.76-2.3Zm-3 3.56a.5.5 0 1 0-.4.92l4.2 1.86v4.68a.5.5 0 0 0 1 0V8.82l4.2-1.86a.5.5 0 1 0-.4-.92L10 7.95l-4.3-1.9Z",
    "#767783",
    "#8C7D9B"
);

const FolderZipGradientIcon = createGradientIcon(
    "ic_folder_zip_gradient",
    "M13 13a1 1 0 0 1 1 1v3.75c0 .14-.11.25-.25.25h-1.5a.25.25 0 0 1-.25-.25V14a1 1 0 0 1 1-1Zm2.5-8A2.5 2.5 0 0 1 18 7.5v7a2.5 2.5 0 0 1-2.5 2.5H15v-3a2 2 0 1 0-4 0v3H4.5A2.5 2.5 0 0 1 2 14.5V8h5.09c.4 0 .78-.16 1.06-.44L10.7 5h4.79Zm-4 6a.5.5 0 0 0 0 1H13v-1h-1.5Zm1.5 0h1.5a.5.5 0 0 0 0-1H13v1Zm-1.5-2a.5.5 0 0 0 0 1H13V9h-1.5ZM13 9h1.5a.5.5 0 0 0 0-1H13v1Zm-1.5-2a.5.5 0 0 0 0 1H13V7h-1.5ZM6.98 3c.47 0 .91.18 1.24.51L9.5 4.8 7.44 6.85a.5.5 0 0 1-.35.15H2V5.5A2.5 2.5 0 0 1 4.5 3h2.48Z",
    "#6C7BFF",
    "#A048FE"
);

const CalendarColorIcon = createGradientIcon(
    "ic_calendar_color",
    "M5.5 3A2.5 2.5 0 0 0 3 5.5V6h14v-.5A2.5 2.5 0 0 0 14.5 3h-9ZM17 7H3v7.5A2.5 2.5 0 0 0 5.5 17h9a2.5 2.5 0 0 0 2.5-2.5V7Zm-9 3a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm-1 4a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm4-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm-1 4a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm4-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z",
    "#FF6C6C",
    "#FE8548"
);

const ContactCardColorIcon = createGradientIcon(
    "ic_contact_card_color",
    "M3.5 4C2.67 4 2 4.67 2 5.5v9c0 .83.67 1.5 1.5 1.5h13c.83 0 1.5-.67 1.5-1.5v-9c0-.83-.67-1.5-1.5-1.5h-13Zm3 6a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Zm-1.55.75h3.1c.53 0 .95.42.95.95 0 .85-.58 1.58-1.4 1.79l-.06.01a4.3 4.3 0 0 1-2.08 0l-.06-.01c-.82-.2-1.4-.94-1.4-1.8 0-.52.42-.94.95-.94ZM11 8.5c0-.28.22-.5.5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5Zm.5 2.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1 0-1Z",
    "#6C7BFF",
    "#A048FE"
);

const DatabaseColorIcon = createGradientIcon(
    "ic_database_color",
    "M10 8c3.31 0 6-1.34 6-3s-2.69-3-6-3-6 1.34-6 3 2.69 3 6 3Zm4.69.02c.47-.24.93-.54 1.31-.9V15c0 1.66-2.69 3-6 3s-6-1.34-6-3V7.12c.38.36.84.66 1.31.9C6.56 8.64 8.22 9 10 9c1.78 0 3.44-.36 4.69-.98Z",
    "#767783",
    "#8C7D9B"
);

const DocumentFlowchartColorIcon = createGradientIcon(
    "ic_document_flowchart_color",
    "M10 6.5V2H5.5C4.67 2 4 2.67 4 3.5v13c0 .83.67 1.5 1.5 1.5h9c.83 0 1.5-.67 1.5-1.5V8h-4.5A1.5 1.5 0 0 1 10 6.5Zm-4 1c0-.28.22-.5.5-.5h2c.28 0 .5.22.5.5v2a.5.5 0 0 1-.5.5H8v1.8L9.2 13H11v-.5c0-.28.22-.5.5-.5h2c.28 0 .5.22.5.5v2a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5V14H9.2l-1.35 1.35a.5.5 0 0 1-.7 0l-1.5-1.5a.5.5 0 0 1 0-.7L7 11.79V10h-.5a.5.5 0 0 1-.5-.5v-2Zm5-1V2.25L15.75 7H11.5a.5.5 0 0 1-.5-.5Z",
    "#EB6CFF",
    "#8248FE"
);

const TextboxColorIcon = createGradientIcon(
    "ic_textbox_color",
    "M17 5.5A2.5 2.5 0 0 0 14.5 3h-9A2.5 2.5 0 0 0 3 5.5v9A2.5 2.5 0 0 0 5.5 17h9a2.5 2.5 0 0 0 2.5-2.5v-9Zm-11 1c0-.28.22-.5.5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5ZM6.5 9h4a.5.5 0 0 1 0 1h-4a.5.5 0 0 1 0-1ZM6 12.5c0-.28.22-.5.5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5Z",
    "#6CE0FF",
    "#4894FE"
);

const TextboxGrayIcon = createGradientIcon(
    "ic_textbox_gray",
    "M17 5.5A2.5 2.5 0 0 0 14.5 3h-9A2.5 2.5 0 0 0 3 5.5v9A2.5 2.5 0 0 0 5.5 17h9a2.5 2.5 0 0 0 2.5-2.5v-9Zm-11 1c0-.28.22-.5.5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5ZM6.5 9h4a.5.5 0 0 1 0 1h-4a.5.5 0 0 1 0-1ZM6 12.5c0-.28.22-.5.5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5Z",
    "#767783",
    "#8C7D9B"
);

const ImageColorIcon = createGradientIcon(
    "ic_image_color",
    "M6 3a3 3 0 0 0-3 3v8c0 .65.2 1.25.56 1.74l5.39-5.3a1.5 1.5 0 0 1 2.1 0l5.4 5.3c.34-.49.55-1.1.55-1.74V6a3 3 0 0 0-3-3H6Zm0 14c-.65 0-1.24-.2-1.73-.55l5.38-5.3c.2-.2.5-.2.7 0l5.38 5.3c-.49.35-1.08.55-1.73.55H6Zm6.5-8.25a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5Z",
    "#74E2A3",
    "#3ED4CC"
);

const ProjectionScreenTextColorIcon = createGradientIcon(
    "ic_projection_screen_text_color",
    "M1 3.5C1 2.67 1.67 2 2.5 2h15a1.5 1.5 0 0 1 .5 2.91V12a3 3 0 0 1-3 3h-4.5v2h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2H5a3 3 0 0 1-3-3V4.91c-.58-.2-1-.76-1-1.41ZM7.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5ZM6 9.5c0 .28.22.5.5.5h7a.5.5 0 0 0 0-1h-7a.5.5 0 0 0-.5.5ZM7.5 11a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5Z",
    "#FFA96C",
    "#FED148"
);

const TableSparkleColorIcon = createGradientIcon(
    "ic_table_sparkle_color",
    "m13.88.28.35 1.07a2.2 2.2 0 0 0 1.4 1.4l1.07.35h.02a.42.42 0 0 1 0 .8l-1.07.35a2.2 2.2 0 0 0-1.4 1.4l-.35 1.07a.42.42 0 0 1-.8 0l-.35-1.07a2.23 2.23 0 0 0-1.4-1.4l-1.07-.35a.42.42 0 0 1 0-.8l1.07-.35a2.2 2.2 0 0 0 1.38-1.4l.35-1.07a.42.42 0 0 1 .8 0Zm4.9 7.93-.76-.25a1.58 1.58 0 0 1-1-1l-.25-.76a.3.3 0 0 0-.57 0l-.25.77a1.58 1.58 0 0 1-.98 1l-.77.24a.3.3 0 0 0 0 .57l.77.25a1.58 1.58 0 0 1 1 1l.24.77a.3.3 0 0 0 .58 0l.24-.77a1.58 1.58 0 0 1 1-1l.77-.24a.3.3 0 0 0 0-.57h-.02ZM9.1 3a1.42 1.42 0 0 0 .86 1.84h.01l1.08.35a1.2 1.2 0 0 1 .76.77l.2.6V7H8V3h1.09Zm7.4 9H13V8.5a1.3 1.3 0 0 0 .87 1.23h.01l.77.25a.58.58 0 0 1 .37.37l.25.77v.01a1.3 1.3 0 0 0 1.22.87Zm-1.99 5H13v-4h4v1.5a2.5 2.5 0 0 1-2.5 2.5ZM8 12h4V8H8v4Zm4 1H8v4h4v-4ZM7 8v4H3V8h4Zm0 5H3v1.5A2.5 2.5 0 0 0 5.5 17H7v-4Zm0-6V3H5.5A2.5 2.5 0 0 0 3 5.5V7h4Z",
    "#74E2A3",
    "#3ED4CC"
);

const DeveloperBoardColorIcon = createGradientIcon(
    "ic_developer_board_color",
    "M10 12a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM8 2.5a.5.5 0 0 0-1 0V4h-.5A2.5 2.5 0 0 0 4 6.5V7H2.5a.5.5 0 0 0 0 1H4v1.5H2.5a.5.5 0 0 0 0 1H4V12H2.5a.5.5 0 0 0 0 1H4v.5A2.5 2.5 0 0 0 6.5 16H7v1.5a.5.5 0 0 0 1 0V16h1.5v1.5a.5.5 0 0 0 1 0V16H12v1.5a.5.5 0 0 0 1 0V16h.5a2.5 2.5 0 0 0 2.5-2.5V13h1.5a.5.5 0 0 0 0-1H16v-1.5h1.5a.5.5 0 0 0 0-1H16V8h1.5a.5.5 0 0 0 0-1H16v-.5A2.5 2.5 0 0 0 13.5 4H13V2.5a.5.5 0 0 0-1 0V4h-1.5V2.5a.5.5 0 0 0-1 0V4H8V2.5Zm5 7.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z",
    "#767783",
    "#8C7D9B"
);

const GifColorIcon = createGradientIcon(
    "ic_gif_color",
    "M15.4 3c1.39 0 2.52 1.19 2.6 2.68v8.48c0 1.51-1.09 2.75-2.45 2.84H4.6c-1.39 0-2.52-1.19-2.6-2.68V5.84C2 4.33 3.09 3.1 4.45 3H15.4ZM6.85 7C5.18 7 4 8.32 4 10c0 1.64 1.26 3 2.85 3 1.2 0 2.07-.8 2.14-1.63l.01-.12V9.9a.52.52 0 0 0-.44-.4h-.1l-.92-.01h-.1A.51.51 0 0 0 7 10c0 .25.2.45.44.5h.48v.83c-.07.32-.47.67-1.07.67-.96 0-1.77-.88-1.77-2 0-1.17.74-2 1.77-2 .46 0 .83.07 1.1.2.26.12.59.02.72-.23.14-.24.03-.55-.24-.67C8 7.1 7.47 7 6.85 7ZM11 7a.5.5 0 0 0-.5.41v5.18a.5.5 0 0 0 1 0V7.41A.5.5 0 0 0 11 7Zm4.5 0h-2a.5.5 0 0 0-.5.41v5.18a.5.5 0 0 0 1 0V11h1.59a.5.5 0 0 0 0-1H14V8h1.59a.5.5 0 0 0 0-1h-.09Z",
    "#74E2A3",
    "#3ED4CC"
);

type IconRenderer = (className?: string) => JSX.Element;

/** Register a list of extensions to a single icon renderer in the lookup map. */
function registerExtensions(map: Map<string, IconRenderer>, extensions: string[], renderer: IconRenderer): void {
    extensions.forEach((ext) => map.set(ext.toLowerCase(), renderer));
}

/** Build the extension → icon lookup map. Executed once at module load. */
function buildExtensionIconMap(): Map<string, IconRenderer> {
    const map = new Map<string, IconRenderer>();

    registerExtensions(map, ["eml", "msg", "ost", "pst"], (cls) => <MailColor className={cls} fontSize={ICON_SIZE} />);
    // GIF files
    registerExtensions(map, ["gif"], (cls) => <GifColorIcon className={cls} />);
    // Archive files
    registerExtensions(
        map,
        ["7z", "ace", "arc", "arj", "dmg", "gz", "iso", "lzh", "pkg", "rar", "sit", "tar", "tgz", "z", "zip"],
        (cls) => <FolderZipGradientIcon className={cls} />
    );
    // Image / design files
    registerExtensions(
        map,
        [
            "ai",
            "arw",
            "bmp",
            "cr2",
            "crw",
            "cvs",
            "dcr",
            "dds",
            "dgn",
            "dib",
            "dng",
            "emf",
            "eps",
            "erf",
            "fig",
            "gdraw",
            "heic",
            "heif",
            "ico",
            "ind",
            "indb",
            "indd",
            "indt",
            "jfi",
            "jfif",
            "jif",
            "jpe",
            "jpeg",
            "jpg",
            "kdc",
            "mrw",
            "nef",
            "orf",
            "oxps",
            "pct",
            "pd",
            "pict",
            "png",
            "pns",
            "ps",
            "psd",
            "raw",
            "sketch",
            "svg",
            "svgz",
            "tga",
            "tif",
            "tiff",
            "wdp",
            "wmf",
            "xd",
            "xps",
        ],
        (cls) => <ImageColorIcon className={cls} />
    );
    registerExtensions(map, ["pdf"], (cls) => <DocumentPdfColorIcon className={cls} />);
    // Presentation files
    registerExtensions(
        map,
        ["gslides", "key", "odp", "pot", "potm", "potx", "pps", "ppsm", "ppsx", "ppt", "pptm", "pptx", "sldm", "sldx"],
        (cls) => <ProjectionScreenTextColorIcon className={cls} />
    );
    // Spreadsheet files
    registerExtensions(
        map,
        ["csv", "gsheet", "numbers", "odc", "ods", "xlc", "xls", "xlsb", "xlsm", "xlsx", "xlt", "xltm", "xltx"],
        (cls) => <TableSparkleColorIcon className={cls} />
    );
    // System / developer files
    registerExtensions(
        map,
        [
            "bak",
            "bin",
            "cab",
            "cache",
            "cat",
            "cer",
            "class",
            "dat",
            "db",
            "dbg",
            "dl_",
            "dll",
            "ithmb",
            "jar",
            "kb",
            "ldt",
            "lrprev",
            "pdb",
            "pkpass",
            "ppa",
            "ppam",
            "rom",
            "thm",
            "thmx",
            "vsl",
            "xla",
            "xlam",
            "xlb",
            "xll",
        ],
        (cls) => <DeveloperBoardColorIcon className={cls} />
    );
    // Audio files
    registerExtensions(
        map,
        [
            "aac",
            "aif",
            "aiff",
            "amr",
            "au",
            "awb",
            "dct",
            "dss",
            "dvf",
            "flac",
            "gsm",
            "m4a",
            "m4p",
            "mid",
            "mmf",
            "mp3",
            "oga",
            "ra",
            "rm",
            "wav",
            "wma",
            "wv",
        ],
        (cls) => <SoundWaveCircleColorIcon className={cls} />
    );

    // Web/HTML files + Code/developer files
    registerExtensions(
        map,
        [
            "abap",
            "ada",
            "adp",
            "ahk",
            "as",
            "as3",
            "asc",
            "ascx",
            "asm",
            "asp",
            "awk",
            "bash",
            "bat",
            "bib",
            "bsh",
            "build",
            "builder",
            "c",
            "c++",
            "capfile",
            "cc",
            "cfc",
            "cfm",
            "cfml",
            "cl",
            "clj",
            "cls",
            "cmake",
            "cmd",
            "coffee",
            "config",
            "cpp",
            "cpt",
            "cpy",
            "cs",
            "cshtml",
            "cson",
            "csproj",
            "css",
            "ctp",
            "cxx",
            "d",
            "ddl",
            "di",
            "disco",
            "dml",
            "dtd",
            "dtml",
            "el",
            "emakefile",
            "erb",
            "erl",
            "f",
            "f90",
            "f95",
            "fs",
            "fsi",
            "fsscript",
            "fsx",
            "gemfile",
            "gemspec",
            "gitconfig",
            "go",
            "groovy",
            "gvy",
            "h",
            "h++",
            "haml",
            "handlebars",
            "hbs",
            "hh",
            "hpp",
            "hrl",
            "hs",
            "htc",
            "htm",
            "html",
            "hxx",
            "idl",
            "iim",
            "inc",
            "inf",
            "ini",
            "inl",
            "ipp",
            "irbrc",
            "jade",
            "jav",
            "java",
            "js",
            "json",
            "jsp",
            "jsx",
            "l",
            "less",
            "lhs",
            "lisp",
            "log",
            "lst",
            "ltx",
            "lua",
            "m",
            "mak",
            "make",
            "manifest",
            "markdn",
            "markdown",
            "master",
            "md",
            "mdown",
            "mht",
            "mkdn",
            "ml",
            "mli",
            "mll",
            "mly",
            "mm",
            "mud",
            "nfo",
            "opml",
            "osascript",
            "p",
            "pas",
            "patch",
            "php",
            "php2",
            "php3",
            "php4",
            "php5",
            "phtml",
            "pl",
            "pm",
            "pod",
            "pp",
            "profile",
            "ps1",
            "ps1xml",
            "psd1",
            "psm1",
            "pss",
            "pt",
            "py",
            "pyw",
            "r",
            "rake",
            "rb",
            "rbx",
            "rc",
            "rdf",
            "re",
            "reg",
            "rest",
            "resw",
            "resx",
            "rhtml",
            "rjs",
            "rprofile",
            "rpy",
            "rss",
            "rst",
            "ruby",
            "rxml",
            "s",
            "sass",
            "scala",
            "scm",
            "sconscript",
            "sconstruct",
            "script",
            "scss",
            "sgml",
            "sh",
            "shtml",
            "sml",
            "sql",
            "sty",
            "svn-base",
            "swift",
            "tcl",
            "tex",
            "textile",
            "tld",
            "tli",
            "tmpl",
            "tpl",
            "vb",
            "vi",
            "vim",
            "vmg",
            "webpart",
            "wsdl",
            "wsp",
            "xaml",
            "xhtml",
            "xml",
            "xoml",
            "xsd",
            "xsl",
            "xslt",
            "yaml",
            "yaws",
            "yml",
            "zsh",
        ],
        (cls) => <CodeBlockColorIcon className={cls} />
    );

    // Calendar files
    registerExtensions(map, ["cal", "ical", "icalendar", "ics", "ifb", "vcs"], (cls) => (
        <CalendarColorIcon className={cls} />
    ));

    // Contact card files
    registerExtensions(map, ["vcf"], (cls) => <ContactCardColorIcon className={cls} />);

    // Database files
    registerExtensions(map, ["accdb", "mdb"], (cls) => <DatabaseColorIcon className={cls} />);

    // Diagramming / flowchart / project files
    registerExtensions(
        map,
        [
            "b",
            "fluid",
            "mpp",
            "mpt",
            "pub",
            "vdw",
            "vdx",
            "vsd",
            "dwg",
            "vsdm",
            "vsdx",
            "vss",
            "vssm",
            "vssx",
            "vst",
            "vstm",
            "vstx",
            "vsx",
            "vsw",
            "xsn",
        ],
        (cls) => <DocumentFlowchartColorIcon className={cls} />
    );

    // Plain-text files (grey)
    registerExtensions(map, ["dif", "diff", "out", "plist", "properties", "readme", "text", "txt"], (cls) => (
        <TextboxGrayIcon className={cls} />
    ));

    // Rich document files (blue)
    registerExtensions(
        map,
        ["doc", "docb", "docm", "docx", "dot", "dotm", "dotx", "epub", "gdoc", "odt", "pages", "rtf", "wri"],
        (cls) => <TextboxColorIcon className={cls} />
    );

    // 3D model files
    registerExtensions(
        map,
        [
            "3ds",
            "3mf",
            "blend",
            "cool",
            "dae",
            "df",
            "dwfx",
            "dxf",
            "fbx",
            "glb",
            "gltf",
            "holo",
            "layer",
            "layout",
            "max",
            "mtl",
            "obj",
            "off",
            "ply",
            "skp",
            "stl",
            "stp",
            "t",
            "thl",
            "x",
        ],
        (cls) => <CubeColorIcon className={cls} />
    );

    // Video files
    registerExtensions(
        map,
        [
            "3g2",
            "3gp",
            "3gp2",
            "3gpp",
            "asf",
            "avi",
            "dvr-ms",
            "flv",
            "m1v",
            "m4v",
            "mkv",
            "mm4p",
            "mod",
            "mov",
            "mp2",
            "mp2v",
            "mp4",
            "mpa",
            "mpe",
            "mpeg",
            "mpg",
            "mpv",
            "mpv2",
            "mts",
            "ogg",
            "qt",
            "swf",
            "ts",
            "vob",
            "webm",
            "wlmp",
            "wm",
            "wmv",
            "wmx",
        ],
        (cls) => <VideoColor className={cls} fontSize={ICON_SIZE} />
    );

    // Generic document (explicit registrations — also the fallback for unrecognized extensions)
    const documentRenderer: IconRenderer = (cls) => <DocumentColor className={cls} fontSize={ICON_SIZE} />;
    registerExtensions(map, ["pbids", "pbix"], documentRenderer);
    registerExtensions(
        map,
        ["apk", "app", "application", "appref-ms", "appx", "exe", "ipa", "msi", "xap"],
        documentRenderer
    );
    registerExtensions(map, ["otf", "ttf", "woff"], documentRenderer);
    registerExtensions(map, ["link", "lnk", "url", "webloc", "website"], documentRenderer);
    registerExtensions(map, ["ms-one-stub", "one", "onepkg", "onetoc", "onetoc2"], documentRenderer);

    return map;
}

const extensionIconMap = buildExtensionIconMap();

/** Returns the appropriate file-type icon for an attachment visual. */
export function getAttachmentIcon(item: AttachmentVisual, className?: string): JSX.Element {
    if (item.type === "folder") {
        return <DocumentFolderColorIcon className={className} />;
    }
    const ext = (item.extension ?? "").toLowerCase();
    const renderer = extensionIconMap.get(ext);
    if (renderer) {
        return renderer(className);
    }
    return <DocumentColor className={className} fontSize={ICON_SIZE} />;
}
